/**
 * board-animations — ЕДИНЫЙ модуль анимаций доски (overlay-эффекты), на который
 * ССЫЛАЮТСЯ экраны (игра + реплей). owner-directive 2026-05-30 «код через модули»:
 * общая для нескольких поверхностей анимационная логика живёт здесь, НЕ копируется
 * в каждый экран.
 *
 * Содержит ровно те overlay-эффекты, которые описаны в
 * `docs/specs/board-feedback-animations.md`:
 *   · line-clear celebration (§2.1) — 3 варианта overlay-шара (direct 600мс /
 *     spawn-grow 800мс / spawn-hold 800мс), движимые контрактом `MoveEvents` (§2.3);
 *   · move-slide (§2.5, T replay-path) — шар едет по BFS-пути `findPath` от
 *     источника к цели (используется в реплее, чтобы показать ход; в живой игре
 *     ход остаётся teleport — out of scope §9, не трогаем).
 *
 * Visual-only: ни один эффект не меняет game-state (state уже посчитан движком);
 * overlay-узлы эфемерны (не в store, не persist). CSS-классы/keyframes — board.css.
 */

import { N } from '@lines98/shared/config/balance';
import type { MoveEvents } from '@lines98/shared/types/game';

// =============================================================================
// Spawn-pop appearance (board-feedback-animations.md §2.4 / §2.5)
// =============================================================================

/**
 * Длительность одноразовой анимации появления шара (board.css `ball-pop` = 0.22s).
 * Запас на доставку animationend → потолок снятия класса.
 */
const SPAWN_POP_MS = 260;

/**
 * Навесить одноразовую анимацию появления `spawn-pop` на шар и снять класс по
 * завершении (animationend + таймер-fallback). База `.ball` без animation, поэтому
 * после снятия последующее снятие `.sel`-bounce / `.shake-invalid` не реанимирует
 * появление (§2.4, AC-11). Общий для живой игры (spawnNext) и реплея (spawned
 * по ходу шары «появляются», §2.5 п.4) — owner-directive «код через модули».
 */
export function applySpawnPop(ball: HTMLElement): void {
  ball.classList.add('spawn-pop');
  const strip = (): void => ball.classList.remove('spawn-pop');
  ball.addEventListener('animationend', strip, { once: true });
  window.setTimeout(strip, SPAWN_POP_MS);
}

// =============================================================================
// Line-clear celebration overlays (board-feedback-animations.md §2.1 / §2.3)
// =============================================================================

/** Длительность direct-move clear overlay (board.css `ball-line-clear` = 0.60s). */
const LINE_CLEAR_ANIM_MS = 600;
/** Длительность spawn-triggered clear overlay с grow-фазой (board.css = 0.80s). */
const LINE_CLEAR_WITH_GROW_MS = 800;
/** Запас на снятие узла после завершения CSS-анимации (буфер). */
const OVERLAY_CLEANUP_BUFFER_MS = 50;

/**
 * Direct-move clear (`viaSpawn:false`): owner's ход САМ замкнул линию. Overlay
 * `line-clear` (600мс): scale 1 → 1.2 → 0. Полноразмерный absolute поверх клетки
 * (перекрывает ре-рендеренный гост, §E-3). `speed` масштабирует длительность
 * (>1 быстрее) — для регулятора скорости реплея; в живой игре speed=1.
 */
export function spawnLineClearOverlay(cell: HTMLElement, color: number, speed = 1): void {
  spawnOverlay(cell, `ball line-clear bc${color}`, LINE_CLEAR_ANIM_MS, speed);
}

/**
 * Spawn-triggered clear, клетка `grown` (next-бал вырос в линию): overlay
 * `line-clear-grow` (800мс) — grow-фаза 0-200мс (scale 0.5→1.0), далее pulse+shrink.
 */
export function spawnLineClearGrowOverlay(cell: HTMLElement, color: number, speed = 1): void {
  spawnOverlay(cell, `ball line-clear-grow bc${color}`, LINE_CLEAR_WITH_GROW_MS, speed);
}

/**
 * Spawn-triggered clear, клетка была real ball: overlay `line-clear-hold` (800мс) —
 * держится at scale 1.0 пока ghosts растут, далее pulse+shrink синхронно.
 */
export function spawnLineClearHoldOverlay(cell: HTMLElement, color: number, speed = 1): void {
  spawnOverlay(cell, `ball line-clear-hold bc${color}`, LINE_CLEAR_WITH_GROW_MS, speed);
}

function spawnOverlay(cell: HTMLElement, className: string, baseMs: number, speed: number): void {
  const overlay = document.createElement('div');
  overlay.className = className;
  // Регулятор скорости реплея масштабирует CSS-длительность анимации.
  if (speed !== 1) overlay.style.animationDuration = `${(baseMs / speed / 1000).toFixed(3)}s`;
  cell.appendChild(overlay);
  window.setTimeout(() => overlay.remove(), baseMs / speed + OVERLAY_CLEANUP_BUFFER_MS);
}

/**
 * Wall-clock длительность (мс) celebration-overlay'ев, которые `celebrate`-проход
 * запустит для данного хода — чтобы вызывающий (автоплей реплея) знал, КОГДА вся
 * визуалка сбора реально догорела, и не стартовал следующий шаг поверх неё
 * (owner-feedback 2026-06-09 «следующий ход не дожидается окончания анимации»).
 *
 * ЕДИНЫЙ источник правды по этим числам — этот модуль (он же их и применяет в
 * `spawnLineClear*Overlay` → `spawnOverlay`). Экран НЕ дублирует 600/800 числом
 * (code-structure §1, hard-rule «никакого хардкода тайминга»): он спрашивает здесь.
 *
 * Возвращает 0, если ход ничего не собрал (overlay'ев нет). Иначе — длительность
 * самого долгого варианта overlay'я этого хода с тем же буфером снятия узла, что
 * у `spawnOverlay`, делённую на `speed` (регулятор скорости реплея ускоряет всё).
 *
 * Контракт `viaSpawn`/`cleared` тот же, что читает `celebrate` (board-feedback-
 * animations.md §2.3) → длительность согласована с реально запущенными overlay'ями
 * по построению.
 */
export function lineClearCelebrationMs(events: MoveEvents | null, speed = 1): number {
  if (!events || events.cleared.length === 0) return 0;
  // viaSpawn → grow/hold overlay'и (800мс); direct-move → line-clear (600мс). Берём
  // базу того варианта, что реально проиграется, + тот же буфер снятия, что в
  // spawnOverlay, и масштабируем регулятором скорости.
  const baseMs = events.viaSpawn ? LINE_CLEAR_WITH_GROW_MS : LINE_CLEAR_ANIM_MS;
  return baseMs / speed + OVERLAY_CLEANUP_BUFFER_MS;
}

// =============================================================================
// Move-slide (board-feedback-animations.md §2.5) — ball travels the BFS path
// =============================================================================

/**
 * Базовая длительность слайда на ОДНУ клетку пути (мс при speed=1).
 *
 * СКОРОСТЬ ПИКСЕЛЕЙ — лимит плавности (owner-feedback 2026-06-01): на 60-Гц экране
 * быстрое движение читается рвано (sample-and-hold: глаз видит дискретные позиции),
 * хотя анимация и идёт на полной частоте. Гладко — только когда скорость пикселей в
 * комфортной зоне (~6–10 px/кадр). 110мс/клетку + низкий пик профиля (1.25×, см.
 * `slideProgress`) выводят x1 примерно на ту скорость пикселей, что owner находит
 * комфортной скорости. Owner-playtest 2026-06-01 (lab-сравнение техник): выбрал
 * заметно более медленный ×1 как дефолт («глазу нормально, кто хочет — ускорит ×2»),
 * а перцептивную плавность на быстром даёт motion-trail (см. echo-константы ниже).
 * Множитель скорости (×0.5/×1/×2) остаётся.
 */
export const MOVE_SLIDE_PER_CELL_MS = 180;
/** Нижняя/верхняя границы общей длительности слайда (мс при speed=1). */
const MOVE_SLIDE_MIN_MS = 300;
const MOVE_SLIDE_MAX_MS = 1300;

/**
 * MOTION-TRAIL (owner-playtest 2026-06-01, выбран в lab-сравнении): за едущим
 * шаром тянется «комета» из полупрозрачных эхо-копий — приём, дающий перцептивную
 * гладкость быстрого движения (маскирует дискретность кадров на 60Гц лучше, чем
 * любая возня с easing/snapping). Owner выбрал вариант «как B, 5 эхо + поворот».
 */
const MOVE_SLIDE_ECHOES = 5;
/**
 * Отставание каждого эхо — доля общей длительности (положительная → эхо показывает
 * позицию шара `i·LAG` периода НАЗАД, т.е. тянется СЗАДИ). Пропорция от totalMs →
 * пространственная длина шлейфа постоянна в долях пути при любой скорости.
 */
const MOVE_SLIDE_ECHO_LAG = 0.022;

export interface MoveSlideHandle {
  /** Снять overlay-шар немедленно (напр. при перемотке/unmount во время слайда). */
  cancel(): void;
}

/**
 * Прогнать слайд-анимацию шара по пути `path` (последовательность клеток
 * from..to, включительно — формат `findPath`). Создаёт ВРЕМЕННЫЙ absolute-overlay
 * шар поверх сетки + шлейф из `MOVE_SLIDE_ECHOES` эхо-копий («комета») и твинит их
 * `transform: translate` по ломаной через центры клеток пути (Web Animations API).
 *
 * MOTION-TRAIL (owner-playtest 2026-06-01, выбран в lab-сравнении техник): за
 * головой тянется шлейф полупрозрачных эхо (СЗАДИ, через положительную задержку) —
 * это и дало искомую перцептивную плавность, особенно на быстром множителе. Эхо
 * едут по тем же кадрам, что и голова, лишь со сдвигом по времени. При доезде голова
 * убирается ЦЕЛИКОМ (идентичный статичный шар клетки её бесшовно заменяет, одна
 * тень), а севший шар поднимается НАД эхо — доезжающий хвост прячется ЗА шаром, не
 * меняя его (ни цвета, ни тени). Хвост и поднятый z-index снимает cleanup.
 *
 * ПЛАВНОСТЬ ТЕМПА (owner-feedback 2026-06-01 «дёргается даже по прямой»): движение
 * получает мягкий ГЛОБАЛЬНЫЙ ease-in-out на ВСЮ траекторию (нежный разгон в начале,
 * плавное торможение в конце — один раз за ход), а НЕ постоянную скорость с резким
 * стартом/стопом (старый `linear` читался механически-дёргано). Ключевая тонкость
 * (MDN / bram.us): easing нельзя вешать посегментно — WAAPI применил бы разгон-
 * торможение на КАЖДОЙ клетке → пульсация темпа (из-за неё прошлый ease-in-out и
 * «дёргался»). Поэтому профиль НЕ задаётся опцией WAAPI, а ЗАПЕКАЕТСЯ в позиции
 * кадров: `easedPathKeyframes` семплирует точки вдоль ломаной в моменты, искажённые
 * `slideProgress` (трапеция разгон→крейсер→торможение, пик скорости лишь 1.25× ради
 * минимума px/кадр в середине), и отдаёт WAAPI готовые кадры с `linear`-таймингом.
 * Глобальный профиль гарантирован by-design, посегментная пульсация невозможна.
 *
 * Плавность (без стуттера на прямой): overlay промоутится на отдельный GPU-слой
 * (will-change:transform + translateZ(0) + backface-visibility:hidden,
 * выставляются inline ДО старта анимации). Глянцевый градиент+тень растрируются
 * ОДИН раз и просто композитятся — анимируется ТОЛЬКО transform, без re-paint,
 * compositor крутит кадры на высокой частоте. По завершении (или `cancel`)
 * will-change снимается (cleanup, чтобы не плодить слои) и overlay удаляется.
 *
 * ANTI-SNAP (owner-feedback 2026-06-01, главная причина дёрганья на прямой): Chrome
 * пиксель-снапит JS/WAAPI-управляемый transform (округляет к целым px/кадр), и
 * `will-change`/`translate3d` это больше не гарантированно отключают. Поэтому в КАЖДЫЙ
 * кадр запекается микроповорот `rotate(ANTI_SNAP_DEG)` — слой перестаёт быть осе-
 * выровненным, snapping отключается, возвращается субпиксельный рендер (канон GSAP-хак).
 * Хак КРОСС-ДВИЖКОВЫЙ: Firefox (Gecko) снапит к device-px ещё агрессивнее → rotate
 * тоже возвращает субпиксель; WebKit обычно уже субпиксельный → rotate там no-op,
 * но безвреден. WAAPI идёт на compositor-потоке на всех трёх движках.
 *
 * КРОСС-БРАУЗЕРНАЯ ПРОВЕРКА (brief-38 §A.1): корректность движения (overlay
 * появляется, голова двигается монотонно с конечными transform'ами, шлейф
 * убирается, шар садится без дубля) автоматически гейтится на chromium+firefox+
 * webkit — `tests/visual/replay-motion.parity.test.ts`. ПЕРЦЕПТИВНАЯ гладкость
 * (субпиксельный джиттер) headless не утверждается → остаётся owner-playtest в 3
 * браузерах (smooth-motion-animation SKILL §8). `composite:'replace'` (см. ниже у
 * `.animate`) фиксирует семантику кадров одинаково на всех движках.
 *
 * Позиции берутся из реального layout клеток (`offsetLeft/Top` + размеры), поэтому
 * работают при любой CSS-формуле размера (board-as-etalon, ADR-0010) — модуль не
 * дублирует sizing-математику.
 *
 * @param boardEl     контейнер сетки (.board) — overlay кладётся сюда (position).
 * @param cells       81 клетка (row-major) — источник геометрии центров.
 * @param color       цвет едущего шара (1..7).
 * @param path        путь findPath (>=2 клетки; <2 → мгновенный onDone без анимации).
 * @param speed       множитель скорости (>1 быстрее); масштабирует длительность.
 * @param onDone      колбэк в момент ПОСАДКИ головы (totalMs): садим шар в
 *                    state-рендере. Тут шар уже стоит, но хвост-комета ЕЩЁ доезжает.
 * @param onTrailDone опц. колбэк, когда вся «комета» (голова + ВСЕ эхо) реально
 *                    убрана с доски (`cleanup` отработал, totalMs + хвост). Это
 *                    «вся анимация слайда действительно закончилась» — отдельный
 *                    сигнал от `onDone`-посадки. Автоплей реплея ждёт ИМЕННО его
 *                    (+ celebration), чтобы следующий шаг не наезжал на хвост
 *                    (owner-feedback 2026-06-09). Идемпотентен (зовётся один раз).
 */
export function animateMoveSlide(
  boardEl: HTMLElement,
  cells: readonly HTMLElement[],
  color: number,
  path: ReadonlyArray<{ r: number; c: number }>,
  speed: number,
  onDone: () => void,
  onTrailDone?: () => void,
): MoveSlideHandle {
  if (path.length < 2) {
    onDone();
    onTrailDone?.();
    return { cancel(): void {} };
  }

  const startCell = cells[path[0]!.r * N + path[0]!.c];
  if (!startCell) {
    onDone();
    onTrailDone?.();
    return { cancel(): void {} };
  }
  // Размер overlay-шара = размер РЕАЛЬНОГО шара (`.ball`, не ghost) в стартовой
  // клетке — ИЗМЕРЯЕМ, а не гадаем «0.72·клетки»: иначе при старте слайда шар
  // «прыгал» в размере (статичный шар прятался, overlay появлялся другого размера).
  // Fallback (нет шара / нулевой размер — напр. jsdom) — 72% клетки.
  const cellW = startCell.offsetWidth;
  const realBall = startCell.querySelector<HTMLElement>('.ball:not(.ghost)');
  const ballSize =
    realBall && realBall.offsetWidth > 0
      ? realBall.offsetWidth
      : Math.max(8, Math.round(cellW * 0.72));

  // Гарантируем относительное позиционирование контейнера ДО замера центров клеток
  // (offsetLeft/Top станут относительны boardEl) — без изменения раскладки.
  const boardPos = getComputedStyle(boardEl).position;
  if (boardPos === 'static') boardEl.style.position = 'relative';

  // Точки = ЦЕНТРЫ клеток пути (относительно boardEl). Центрирование шара на точке
  // делается отрицательным half-size margin в makeBall (а НЕ вычитанием полуразмера
  // здесь) — чтобы ОДНИ и те же кадры годились и голове, и более мелким эхо: все
  // коаксиальны на пути. Иначе эхо (меньше головы) при общих кадрах смещались бы
  // углом в top-left → хвост и голова на разных осях (owner-feedback 2026-06-01).
  const points = path.map((p) => {
    const cell = cells[p.r * N + p.c]!;
    return {
      x: cell.offsetLeft + cell.offsetWidth / 2,
      y: cell.offsetTop + cell.offsetHeight / 2,
    };
  });

  const totalMs = clampDuration((path.length - 1) * MOVE_SLIDE_PER_CELL_MS) / speed;

  // Фабрика overlay-шара. Глянец как у реального шара; промоушен на GPU-слой
  // (will-change/backface inline + translate3d в кадрах) → глянец растрируется
  // один раз, анимируется ТОЛЬКО transform, без re-paint на кадр.
  const makeBall = (sizePx: number, opacity: number, z: number, shadow: boolean): HTMLElement => {
    const el = document.createElement('div');
    el.className = `ball static bc${color} move-slide-ball`;
    el.style.position = 'absolute';
    el.style.width = `${sizePx}px`;
    el.style.height = `${sizePx}px`;
    // Центрируем шар на точке пути НЕЗАВИСИМО от размера: отрицательный half-size
    // margin → translate ведёт ЦЕНТР шара, а не угол. Так голова и более мелкие эхо
    // едут по общим кадрам строго коаксиально (без бокового смещения хвоста).
    el.style.marginTop = `${-sizePx / 2}px`;
    el.style.marginLeft = `${-sizePx / 2}px`;
    el.style.pointerEvents = 'none';
    el.style.opacity = `${opacity}`;
    el.style.zIndex = `${z}`;
    // Эхо-копии шлейфа НЕ отбрасывают тень: drop-shadow `.ball` смещён ВНИЗ, и у
    // концентрично сходящегося хвоста тени копятся → тёмный «полумесяц» снизу шара,
    // исчезающий при снятии хвоста (owner-feedback 2026-06-01). Тень — ТОЛЬКО у
    // головы (= как у реального / статичного шара клетки).
    if (!shadow) el.style.boxShadow = 'none';
    el.style.willChange = 'transform';
    el.style.backfaceVisibility = 'hidden';
    boardEl.appendChild(el);
    return el;
  };

  const head = makeBall(ballSize, 1, MOVE_SLIDE_ECHOES + 1, true);

  // Среда без WAAPI (старый jsdom-тест): садим сразу в конечную точку, без шлейфа.
  if (typeof head.animate !== 'function') {
    const last = points[points.length - 1]!;
    head.style.transform = `translate(${last.x}px, ${last.y}px)`;
    let done = false;
    const finishNoWaapi = (): void => {
      if (done) return;
      done = true;
      head.remove();
      onDone();
      onTrailDone?.(); // нет шлейфа → хвост «убран» в тот же момент
    };
    window.setTimeout(finishNoWaapi, totalMs);
    return { cancel: finishNoWaapi };
  }

  // Кадры (позиции вдоль ломаной с запечённым трапец-профилем + анти-снап поворот) —
  // ОБЩИЕ для головы и всех эхо: они едут по одному пути, отличаются лишь задержкой.
  const keyframes = easedPathKeyframes(points, totalMs);

  // Голова + MOVE_SLIDE_ECHOES эхо = «комета». Эхо стартуют с ПОЛОЖИТЕЛЬНОЙ
  // задержкой → показывают позицию `i·LAG` периода назад → тянутся СЗАДИ.
  // Прозрачность и размер убывают к хвосту. fill:'both' — до своей задержки эхо
  // «ждёт» в стартовой клетке (а не у origin доски).
  const sliders: Array<{ el: HTMLElement; anim: Animation }> = [];
  for (let i = 0; i <= MOVE_SLIDE_ECHOES; i++) {
    const el =
      i === 0
        ? head
        : makeBall(
            Math.max(6, Math.round(ballSize * (1 - i * 0.045))),
            Math.max(0.06, 0.55 - (i - 1) * (0.5 / MOVE_SLIDE_ECHOES)),
            MOVE_SLIDE_ECHOES + 1 - i,
            false,
          );
    const anim = el.animate(keyframes, {
      duration: totalMs,
      easing: 'linear',
      fill: 'both',
      delay: i * MOVE_SLIDE_ECHO_LAG * totalMs,
      // `composite: 'replace'` явно (это и дефолт): каждый кадр ПОЛНОСТЬЮ задаёт
      // transform (translate3d + anti-snap rotate уже запечены). Без явного
      // 'replace' Firefox/WebKit при `fill:'both'` исторически по-разному решали,
      // аккумулировать ли с базовым transform слоя → редкий кросс-движковый дрейф
      // позиции эхо. Фиксируем семантику одинаково на всех движках (§A.1).
      composite: 'replace',
    });
    sliders.push({ el, anim });
  }

  let seated = false;
  let cleaned = false;
  let raisedDestBall: HTMLElement | null = null;

  const stripBall = (s: { el: HTMLElement; anim: Animation }): void => {
    s.anim.cancel();
    s.el.style.willChange = 'auto'; // явный cleanup GPU-слоя (UI-perf дисциплина)
    s.el.remove();
  };

  // Голова доехала → УБИРАЕМ её целиком: статичный шар клетки, который сейчас
  // отрисует onDone, идентичен ей по позиции/размеру/тени (клетка без padding/border,
  // place-items:center → центр совпадает пиксель-в-пиксель), поэтому подмена бесшовна
  // и тень всегда РОВНО ОДНА (без наложения двух теней и без рассинхрона inset-тени).
  // Затем ПОДНИМАЕМ севший статичный шар НАД эхо (z выше хвоста) — доезжающий хвост
  // прячется ЗА шаром, а не наплывает поверх. Итог: при остановке с шаром не
  // происходит НИЧЕГО — ни цвета, ни тени, ни моргания (owner-feedback 2026-06-01).
  const seat = (): void => {
    if (seated) return;
    seated = true;
    stripBall(sliders[0]!);
    onDone();
    const dest = path[path.length - 1]!;
    const destBall = cells[dest.r * N + dest.c]?.querySelector<HTMLElement>('.ball:not(.ghost)');
    if (destBall) {
      destBall.style.zIndex = `${MOVE_SLIDE_ECHOES + 2}`;
      raisedDestBall = destBall;
    }
  };

  // Полная уборка хвоста — после доезда последнего эхо ИЛИ при отмене/перемотке.
  // Снимаем поднятый z-index с севшего шара (вернётся к норме; следующий render его
  // и так пересоздаст). Идемпотентна.
  const cleanup = (): void => {
    if (cleaned) return;
    cleaned = true;
    for (const s of sliders) stripBall(s);
    if (raisedDestBall) raisedDestBall.style.zIndex = '';
    // Хвост-комета полностью убрана → «вся анимация слайда закончилась». Это и есть
    // сигнал, которого ждёт автоплей реплея (отдельный от onDone-посадки). Guard
    // `cleaned` делает вызов идемпотентным (в т.ч. при cancel = seat()+cleanup()).
    onTrailDone?.();
  };

  sliders[0]!.anim.addEventListener('finish', seat, { once: true });
  // Safety-net на seat (вкладка скрыта → 'finish' может не прийти).
  window.setTimeout(seat, totalMs + 80);
  // Хвост: последний эхо финиширует на totalMs + ECHOES·LAG·totalMs (+буфер).
  window.setTimeout(cleanup, totalMs + MOVE_SLIDE_ECHOES * MOVE_SLIDE_ECHO_LAG * totalMs + 60);

  return {
    cancel(): void {
      seat();
      cleanup();
    },
  };
}

function clampDuration(ms: number): number {
  return Math.min(MOVE_SLIDE_MAX_MS, Math.max(MOVE_SLIDE_MIN_MS, ms));
}

interface Pt {
  x: number;
  y: number;
}

/** Один WAAPI-кадр: позиция (transform) на равномерном временно́м offset (0..1). */
type PathKeyframe = {
  transform: string;
  offset: number;
};

/**
 * Минимальная длительность кадра при семплинге (мс). Шаг ~12мс ≈ запас под 60–120Гц:
 * между сэмплами компоновщик доинтерполирует линейно, а на такой густоте линейная
 * аппроксимация ease-кривой визуально неотличима от гладкой. Клампим число кадров,
 * чтобы и короткий слайд был плавным, и длинный не плодил лишние кадры.
 */
const EASE_SAMPLE_STEP_MS = 12;
const EASE_SAMPLES_MIN = 16;
const EASE_SAMPLES_MAX = 64;

/**
 * Микро-наклон (градусы), запекаемый в каждый кадр transform. Делает слой НЕ
 * строго осе-выровненным → отключает Chrome-овский pixel-snapping JS/WAAPI-
 * управляемых трансформаций (главная причина дёрганья на прямой; `will-change`/
 * `translate3d` его больше не гарантированно отключают). 0.01° визуально неотличим.
 * Канон GSAP-хак (`rotation: 0.01`).
 */
const ANTI_SNAP_DEG = 0.01;

/**
 * Доля времени на КАЖДЫЙ скос трапеции скорости (разгон в начале / торможение в
 * конце). Середина (1 − 2·RAMP) едет с ПОСТОЯННОЙ скоростью. RAMP=0.2 → 60% пути
 * на крейсере, пик скорости лишь 1/(1−RAMP)=1.25× средней (минимизирует px/кадр в
 * середине — критично для плавности на 60Гц), а скосы 20%/20% убирают резкий
 * старт/стоп. Меньше RAMP → ниже пик, но резче концы; больше → мягче концы, выше пик.
 */
const SLIDE_RAMP = 0.2;

/**
 * Трапециевидный профиль прогресса s(t), t∈[0..1]: линейный разгон [0..RAMP] →
 * ПОСТОЯННАЯ скорость (крейсер) [RAMP..1−RAMP] → линейное торможение [1−RAMP..1].
 * Постоянная скорость в середине — самый гладкий тип движения (нулевое ускорение),
 * а низкий пик (1.25×) держит px/кадр в комфортной зоне. Скорость непрерывна на
 * стыках (C1). Концы ровно 0 и 1 (шар стартует в source-клетке, доезжает в dest,
 * контракт §2.5). Заменил sine-in-out (пик 1.57×), который «разгонял» именно
 * середину прямого хода — owner-feedback 2026-06-01 «дёргается в процессе езды».
 */
function slideProgress(t: number): number {
  const p = SLIDE_RAMP;
  const vc = 1 / (1 - p); // крейсерская скорость, нормировка ∫=1
  if (t < p) return (vc * t * t) / (2 * p); // разгон (s = ½·v·t, v=vc·t/p)
  if (t > 1 - p) {
    const d = 1 - t;
    return 1 - (vc * d * d) / (2 * p); // торможение (симметрично)
  }
  return (vc * p) / 2 + vc * (t - p); // крейсер (постоянная скорость)
}

/**
 * Готовые WAAPI-кадры с ЗАПЕЧЁННЫМ глобальным профилем: на равномерных временны́х
 * offset t=k/K позиция берётся по arc-length-доле `slideProgress(t)` вдоль ломаной.
 * Так профиль (разгон→крейсер→торможение) применяется РОВНО ОДИН раз на всю
 * траекторию (а не посегментно — это убирает пульсацию темпа между клетками); при
 * этом скорость вдоль самой ломаной пропорциональна длине сегментов (длинные не
 * «ускоряются»). Опция WAAPI `easing` при этих кадрах должна быть 'linear'.
 */
function easedPathKeyframes(points: readonly Pt[], totalMs: number): PathKeyframe[] {
  const cum: number[] = [0];
  for (let i = 1; i < points.length; i++) {
    const dx = points[i]!.x - points[i - 1]!.x;
    const dy = points[i]!.y - points[i - 1]!.y;
    cum.push(cum[i - 1]! + Math.hypot(dx, dy));
  }
  const total = cum[cum.length - 1]! || 1;

  const samples = Math.min(
    EASE_SAMPLES_MAX,
    Math.max(EASE_SAMPLES_MIN, Math.round(totalMs / EASE_SAMPLE_STEP_MS)),
  );
  const frames: PathKeyframe[] = [];
  for (let k = 0; k <= samples; k++) {
    const t = k / samples;
    const p = posAtArcFraction(points, cum, total, slideProgress(t));
    // `rotate(${ANTI_SNAP_DEG}deg)` — микроповорот, ломающий осе-выравнивание слоя:
    // без него Chrome пиксель-снапит JS/WAAPI-управляемый transform (округляет к
    // целым px каждый кадр) → неровные шаги = дёрганье на прямой, тем заметнее чем
    // быстрее (подтверждено GSAP/Chrome-доками). Наклон 0.01° визуально неотличим,
    // но возвращает субпиксельный рендер → гладкое движение.
    frames.push({
      transform: `translate3d(${p.x.toFixed(2)}px, ${p.y.toFixed(2)}px, 0) rotate(${ANTI_SNAP_DEG}deg)`,
      offset: t,
    });
  }
  return frames;
}

/** Позиция на arc-length-доле `s`∈[0..1] вдоль ломаной (линейная интерп. внутри сегмента). */
function posAtArcFraction(points: readonly Pt[], cum: readonly number[], total: number, s: number): Pt {
  const target = s * total;
  if (target <= 0) return points[0]!;
  if (target >= total) return points[points.length - 1]!;
  let i = 1;
  while (i < points.length && cum[i]! < target) i++;
  const segLen = cum[i]! - cum[i - 1]!;
  const f = segLen > 0 ? (target - cum[i - 1]!) / segLen : 0;
  return {
    x: points[i - 1]!.x + (points[i]!.x - points[i - 1]!.x) * f,
    y: points[i - 1]!.y + (points[i]!.y - points[i - 1]!.y) * f,
  };
}
